/**
 * One place that turns an amount into text (TCH-06-FR-30).
 *
 * There were ten of these, each a `const usd = ...` copied into a screen, each
 * hardcoding USD, and two of them formatting in a different locale so the same
 * amount rendered as "$1,234.50" on one screen and "USD 1,234.50" on another.
 * None of them knew what the number they were labelling actually was — the API
 * did not say, so each screen decided for itself.
 *
 * Now the API says, and this is the only thing that draws the label.
 */

/** Currencies the platform admits. Mirrors the backend kernel constant. */
export const SUPPORTED_CURRENCIES = ['USD'] as const;

export type SupportedCurrency = (typeof SUPPORTED_CURRENCIES)[number];

/** What an amount is denominated in when a payload predates the field. */
export const PLATFORM_CURRENCY: SupportedCurrency = 'USD';

const LOCALE = 'es-MX';

/**
 * Formats an amount in the currency the API reported.
 *
 * `currency` is deliberately required: passing it is how a screen shows it
 * knows what it is drawing. Payloads written before the field existed pass
 * `undefined`, which reads as the platform currency — the same rule the
 * backend applies to the rows themselves.
 */
export const formatMoney = (
  amount: number | null | undefined,
  currency: string | null | undefined,
): string => {
  if (amount === null || amount === undefined || !Number.isFinite(amount)) return '—';
  const code = (currency || PLATFORM_CURRENCY).toUpperCase();
  try {
    return amount.toLocaleString(LOCALE, { style: 'currency', currency: code });
  } catch {
    // Intl throws on a code it does not recognise. Showing the number with the
    // code beside it is wrong-looking but honest; silently dropping to a
    // dollar sign would state a currency the amount is not in.
    return `${amount.toFixed(2)} ${code}`;
  }
};
